import { Button } from "@/components/Primitives/Button"
import { Input } from "@/components/Primitives/Input"
import { Label } from "@/components/Primitives/Label"
import { useConfig } from "@/hooks/useConfig"
import { Text } from "@/components/Primitives/Text"
import { MarkdownRenderer } from "@/components/Primitives/MarkdownRenderer"
import { Card } from "@/components/Primitives"
import { GoogleOAuthButton } from "../GoogleOAuthButton"

interface SignupFormProps extends React.ComponentPropsWithoutRef<"div"> {
  email: string
  onEmailChange: (email: string) => void
  onSubmit: () => Promise<void>
  isSubmitting: boolean
  error?: string
}

export function SignupForm({
  email,
  onEmailChange,
  onSubmit,
  isSubmitting = false,
  error,
}: SignupFormProps) {
  const { t, tp } = useConfig()

  const loginParagraph = tp("config_sign_up_login", {
    login_text: t("config_sign_up_login_text"),
    login_url: "/login",
  })

  const footerParagraph = tp("config_sign_up_footer", {
    terms_text: t("config_sign_up_terms_text"),
    terms_url: "https://www.pandorasway.com/terms",
    privacy_text: t("config_sign_up_privacy_text"),
    privacy_url: "https://www.pandorasway.com/privacy",
  })

  return (
    <Card className="md:h-full">
      <header className="mb-2">
        <Text variant="h2" bold className="text-2xl">
          {t("config_sign_up_title")}
        </Text>
        <Text className="text-sm text-neutral-700">
          {t("config_sign_up_description")}
        </Text>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <fieldset>
          <fieldset className="grid gap-2 mb-3">
            <Label htmlFor="email" className="font-medium text-sm">
              {t("config_sign_up_email_label")}
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="email"
              placeholder={t("config_sign_up_email_placeholder")}
              value={email}
              required
              className="w-full"
              onChange={(event) => onEmailChange(event.target.value)}
            />
          </fieldset>

          <Button
            type="submit"
            className="w-full"
            data-testid="submit"
            disabled={isSubmitting || !email}
          >
            {isSubmitting
              ? t("config_sign_up_button_submitting")
              : t("config_sign_up_button_continue")}
          </Button>

          {error && (
            <Text className="text-red-500 text-xs text-center mt-2">
              {error}
            </Text>
          )}

          <GoogleOAuthButton navigateUrl="/signup/onboarding" />

          <MarkdownRenderer
            variant="small"
            className="mt-6 text-neutral-700"
            content={loginParagraph}
          />
        </fieldset>

        <footer>
          <MarkdownRenderer
            variant="small"
            className="mt-6 text-neutral-700"
            content={footerParagraph}
          />
        </footer>
      </form>
    </Card>
  )
}
